import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import { useAuth } from "../context/AuthProvider";
import "./AdminKabilesMobile.css";

interface DeletedKabile {
  _id: string;
  name: string;
  members: any[];
  isDeleted: boolean;
  createdAt: string;
}

interface CleanupResult {
  success: boolean;
  dryRun: boolean;
  kabileCount: number;
  totalRemoved: {
    kabileAnswersCount: number;
    kabileSelectedAnswersCount: number;
    globalVotesCount: number;
    userReferencesCount: number;
    invitationsCount: number;
    memberRequestsCount: number;
    notificationsCount: number;
    cacheInvalidatedCount: number;
    physicallyDeletedCount: number;
  };
}

export default function AdminKabilesMobile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [deletedKabiles, setDeletedKabiles] = useState<DeletedKabile[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    deleted: 0,
    empty: 0
  });
  
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<'stats' | 'list' | 'cleanup'>('stats');

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate("/");
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await apiGet<{
        ok: true;
        stats: typeof stats;
        deletedKabiles: DeletedKabile[];
      }>("/api/admin/kabile/stats");
      
      setStats(response.stats);
      setDeletedKabiles(response.deletedKabiles);
    } catch (error) {
      console.error("Error loading kabile data:", error);
      alert("Kabile verileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleDryRun = async () => {
    try {
      setCleanupLoading(true);
      const response = await apiPost<CleanupResult>(
        "/api/admin/kabile/cleanup-deleted",
        { dryRun: true }
      );
      setCleanupResult(response);
      setActiveTab('cleanup');
    } catch (error) {
      console.error("Dry-run failed:", error);
      alert("Ön izleme başarısız oldu");
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleRealCleanup = async () => {
    if (!window.confirm("⚠️ Tüm silinmiş kabileler kalıcı olarak silinecektir. Devam etmek istediğiniz emin misiniz?")) {
      return;
    }

    try {
      setCleanupLoading(true);
      const response = await apiPost<CleanupResult>(
        "/api/admin/kabile/cleanup-deleted",
        { dryRun: false }
      );
      setCleanupResult(response);
      await loadData();
      setShowConfirm(false);
    } catch (error) {
      console.error("Cleanup failed:", error);
      alert("Silme işlemi başarısız oldu");
    } finally {
      setCleanupLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-kabiles-mobile">
        <div className="loading">
          <div className="spinner"></div>
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-kabiles-mobile">
      {/* Header */}
      <div className="mobile-header">
        <h1>🏛️ Kabile Yönetimi</h1>
        <button className="close-btn" onClick={() => navigate(-1)}>✕</button>
      </div>

      {/* Tab Navigation */}
      <div className="tab-nav">
        <button 
          className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          📊 İstatistikler
        </button>
        <button 
          className={`tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          ❌ Silinmiş ({stats.deleted})
        </button>
        <button 
          className={`tab ${activeTab === 'cleanup' ? 'active' : ''}`}
          onClick={() => setActiveTab('cleanup')}
        >
          🧹 Cleanup
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="stats-section">
            <div className="stat-item">
              <div className="stat-icon">📈</div>
              <div className="stat-info">
                <div className="stat-label">Toplam Kabile</div>
                <div className="stat-value">{stats.total}</div>
              </div>
            </div>

            <div className="stat-item active">
              <div className="stat-icon">✅</div>
              <div className="stat-info">
                <div className="stat-label">Aktif Kabile</div>
                <div className="stat-value">{stats.active}</div>
              </div>
            </div>

            <div className="stat-item deleted">
              <div className="stat-icon">❌</div>
              <div className="stat-info">
                <div className="stat-label">Silinmiş Kabile</div>
                <div className="stat-value">{stats.deleted}</div>
              </div>
            </div>

            <div className="stat-item empty">
              <div className="stat-icon">🗑️</div>
              <div className="stat-info">
                <div className="stat-label">0 Üyeli (Silinebilir)</div>
                <div className="stat-value">{stats.empty}</div>
              </div>
            </div>

            <div className="info-box">
              <h3>ℹ️ Bilgi</h3>
              <p>0 üyeli silinmiş kabileler fiziksel olarak silinebilir ve isimleri tekrar kullanılabilir hale gelebilir.</p>
            </div>
          </div>
        )}

        {/* List Tab */}
        {activeTab === 'list' && (
          <div className="list-section">
            {deletedKabiles.length === 0 ? (
              <div className="empty-state">
                <p>Silinmiş kabile yok</p>
              </div>
            ) : (
              <div className="kabile-list">
                {deletedKabiles.map((kabile) => (
                  <div key={kabile._id} className={`kabile-card ${kabile.members.length === 0 ? 'empty' : ''}`}>
                    <div className="card-header">
                      <div className="card-title">{kabile.name}</div>
                      <div className={`badge ${kabile.members.length === 0 ? 'empty' : 'warning'}`}>
                        {kabile.members.length === 0 ? '🗑️ Silinebilir' : `⚠️ ${kabile.members.length} üye`}
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="info-row">
                        <span className="label">Üye Sayısı:</span>
                        <span className="value">{kabile.members.length}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">Oluşturulma:</span>
                        <span className="value">{new Date(kabile.createdAt).toLocaleDateString('tr-TR')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cleanup Tab */}
        {activeTab === 'cleanup' && (
          <div className="cleanup-section">
            <div className="cleanup-info">
              <p className="info-text">
                {stats.empty} adet 0 üyeli kabile fiziksel olarak silinebilir.
              </p>
            </div>

            <div className="cleanup-buttons">
              <button 
                onClick={handleDryRun}
                disabled={cleanupLoading || stats.empty === 0}
                className="btn btn-preview"
              >
                {cleanupLoading ? "⏳" : "👁️"} Ön İzleme
              </button>
              
              {stats.empty > 0 && (
                <>
                  {!showConfirm ? (
                    <button 
                      onClick={() => setShowConfirm(true)}
                      disabled={cleanupLoading}
                      className="btn btn-danger"
                    >
                      🗑️ Silmeyi Onayla
                    </button>
                  ) : (
                    <>
                      <button 
                        onClick={handleRealCleanup}
                        disabled={cleanupLoading}
                        className="btn btn-destroy"
                      >
                        {cleanupLoading ? "⏳" : "✓"} Evet, Sil
                      </button>
                      <button 
                        onClick={() => setShowConfirm(false)}
                        disabled={cleanupLoading}
                        className="btn btn-cancel"
                      >
                        ✕ İptal
                      </button>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Cleanup Sonuçları */}
            {cleanupResult && (
              <div className={`cleanup-result ${cleanupResult.success ? 'success' : 'warning'}`}>
                <h3>
                  {cleanupResult.dryRun ? "📋 Ön İzleme" : "✅ Tamamlandı"}
                </h3>
                
                <div className="result-items">
                  <div className="result-item">
                    <span>İşlem Yapılan Kabile</span>
                    <strong>{cleanupResult.kabileCount}</strong>
                  </div>
                  <div className="result-item">
                    <span>Fiziksel Silinen</span>
                    <strong>{cleanupResult.totalRemoved.physicallyDeletedCount}</strong>
                  </div>
                  <div className="result-item">
                    <span>Silinen Notif</span>
                    <strong>{cleanupResult.totalRemoved.notificationsCount}</strong>
                  </div>
                  <div className="result-item">
                    <span>Güncellenen User</span>
                    <strong>{cleanupResult.totalRemoved.userReferencesCount}</strong>
                  </div>
                  <div className="result-item">
                    <span>Cache Silinen</span>
                    <strong>{cleanupResult.totalRemoved.cacheInvalidatedCount}</strong>
                  </div>
                </div>

                {cleanupResult.dryRun && (
                  <div className="info-box">
                    ℹ️ Bu ön izlemedir. Gerçekten silmek için "Evet, Sil"'e tıklayın.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
